import { Link } from '@tanstack/react-router';
import { Button, Separator } from '@upcaret/ui';
import { Facebook, Github, Linkedin, Mail, Phone } from 'lucide-react';
import {
  UPCARET_BUSINESS_PHONE_DISPLAY,
  UPCARET_BUSINESS_PHONE_TEL,
  UPCARET_BUSINESS_PHONE_VISIBLE,
} from '../config/site-contact';
import { BrandLink } from './brand-link';


export default function Footer() {
  return (
    <footer className="mt-16 border-t border-slate-200 bg-white/70">
      <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4 md:gap-12">
          {/* Brand Section */}
          <div className="space-y-4">
            <BrandLink />
            <p className="max-w-[280px] text-sm text-slate-600">
              Helping teams get hours back for the work that matters.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-primary"
              >
                <Link to="/contact" className="inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Talk with us
                </Link>
              </Button>
              {UPCARET_BUSINESS_PHONE_VISIBLE ? (
                <a
                  href={UPCARET_BUSINESS_PHONE_TEL}
                  className="inline-flex items-center gap-2 text-sm text-slate-600 underline-offset-4 hover:text-primary hover:underline"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  {UPCARET_BUSINESS_PHONE_DISPLAY}
                </a>
              ) : null}
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9 border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-primary"
                  asChild
                >
                  <a
                    href="https://github.com/chmoder"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center"
                  >
                    <Github className="h-3.5 w-3.5" />
                    <span className="sr-only">GitHub</span>
                  </a>
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9 border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-primary"
                  asChild
                >
                  <a
                    href="https://www.linkedin.com/company/upcaret"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center"
                  >
                    <Linkedin className="h-3.5 w-3.5" />
                    <span className="sr-only">LinkedIn</span>
                  </a>
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-9 w-9 border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-primary"
                  asChild
                >
                  <a
                    href="https://www.facebook.com/upcaret"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center"
                  >
                    <Facebook className="h-3.5 w-3.5" />
                    <span className="sr-only">Facebook</span>
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Quick Links
            </h3>
            <nav className="flex flex-col space-y-2">
              <Link
                to="/"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Home
              </Link>
              <Link
                to="/products/website-agent"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Website Agent
              </Link>
              <Link
                to="/products/internal-agent"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Internal Agent
              </Link>
              <Link
                to="/products/extraction-api"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Extraction API
              </Link>
              <Link
                to="/about"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                About
              </Link>
              <Link
                to="/products/agent/demos"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Demos
              </Link>
              <Link
                to="/contact"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Contact us
              </Link>
            </nav>
          </div>

          {/* Product Links */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              upCaret Products
            </h3>
            <nav className="flex flex-col space-y-2">
              <Link
                to="/products/website-agent"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Website Agent
              </Link>
              <Link
                to="/products/internal-agent"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Internal Agent
              </Link>
              <Link
                to="/products/extraction-api"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Extraction API
              </Link>
              <a
                href="https://cently.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Cently
              </a>
              <a
                href="https://chazown.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Chazown
              </a>
            </nav>
          </div>

          {/* Legal Links */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Legal
            </h3>
            <nav className="flex flex-col space-y-2">
              <Link
                to="/privacy"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-slate-700 transition-colors hover:text-primary"
              >
                Terms of Service
              </Link>
            </nav>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
          <p className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} upCaret. All rights reserved.
          </p>
          <p className="text-sm text-slate-500">Built by the team at upCaret</p>
        </div>
      </div>
    </footer>
  );
}
